// The things you can spend, drawn once and used everywhere: on a card's
// cost rail, on its gain line, and in the strip at the top of the phone.
//
// Matt: "The cost for these cards should be on the top left edge of the
// card in icons... And then I need to be able to see what I have. Anything
// that I could spend on cards needs to be visible on this screen... I can
// glance at the top and see what my current resources are."
//
// Same glyph, same colour, in all three places - that is the whole point.
// Shape carries the meaning as well as colour, so the strip still reads
// without colour vision. Line art in a 16x16 box, stroked in currentColor.

export const RESOURCES = {
  workers: {
    label: 'workers',
    one: 'worker',
    colour: '#e0b45a',
    art: '<circle cx="8" cy="4.4" r="2.8"/><path d="M2.6 14.6c0-3.2 2.4-5.2 5.4-5.2s5.4 2 5.4 5.2"/>',
  },
  food: {
    label: 'food',
    colour: '#7fc26a',
    art: '<path d="M8 15V5.6"/><path d="M8 5.6C5.6 4.5 4.2 2.5 4.6 .6 7.1 1 8.6 3 8 5.6Z"/><path d="M8 5.6c2.4-1.1 3.8-3.1 3.4-5C8.9 1 7.4 3 8 5.6Z"/><path d="M8 10.6c-2-.9-3.3-2.2-3.6-3.9 2.3-.2 3.8 1.1 3.6 3.9Z"/><path d="M8 10.6c2-.9 3.3-2.2 3.6-3.9-2.3-.2-3.8 1.1-3.6 3.9Z"/>',
  },
  goods: {
    label: 'goods',
    colour: '#c79a5c',
    art: '<path d="M1.6 5 8 1.8 14.4 5v6L8 14.2 1.6 11Z"/><path d="M1.6 5 8 8.2 14.4 5M8 8.2v6"/>',
  },
  coin: {
    label: 'coin',
    colour: '#e8cf63',
    art: '<circle cx="8" cy="8" r="6"/><circle cx="8" cy="8" r="2.3"/>',
  },
  knowledge: {
    label: 'knowledge',
    colour: '#86b3e0',
    art: '<path d="M8 4.4C6.4 3 4.4 2.5 2 2.8v9c2.4-.3 4.4.2 6 1.6 1.6-1.4 3.6-1.9 6-1.6v-9c-2.4-.3-4.4.2-6 1.6Z"/><path d="M8 4.4V13"/>',
  },
  happiness: {
    label: 'happiness',
    colour: '#f0a6c0',
    art: '<circle cx="8" cy="8" r="5.6"/><path d="M5.5 9.3a3.1 3.1 0 0 0 5 0"/><circle cx="6.1" cy="6.4" r=".9" fill="currentColor" stroke="none"/><circle cx="9.9" cy="6.4" r=".9" fill="currentColor" stroke="none"/>',
  },
  population: {
    label: 'people',
    one: 'person',
    colour: '#cfc6b2',
    art: '<circle cx="5.6" cy="4.8" r="2.3"/><path d="M1.4 14c0-2.6 1.9-4.2 4.2-4.2S9.8 11.4 9.8 14"/><circle cx="11.6" cy="5.4" r="1.9"/><path d="M10.2 9.7c2.3-.4 4.4 1.1 4.4 4"/>',
  },
  // Half a person's day: the Stewards' token. Matt: "we need an icon for a
  // half a day... so that it can read like the others." An hourglass, because
  // what you are spending is time rather than people.
  // The Frontier's token (2026-10-15): two a worker, like shifts. A boot
  // print, because an action out there is mostly a step.
  actions: {
    label: 'actions',
    one: 'action',
    colour: '#e07a5a',
    art: '<path d="M6.2 2.4c1.6 0 2.4 1.5 2.4 3.4 0 1.6-.8 2.7-.8 3.9H4.6c0-1.3-.9-2.4-.9-4 0-1.8 1-3.3 2.5-3.3Z"/><path d="M4.6 11.4h3.2v.9a1.6 1.6 0 0 1-3.2 0Z"/><path d="M10.6 5.6c1.1 0 1.8 1.1 1.8 2.5 0 1.1-.6 2-.6 2.9H9.4c0-.9-.6-1.8-.6-2.9 0-1.4.7-2.5 1.8-2.5Z"/><path d="M9.4 12.6h2.4v.7a1.2 1.2 0 0 1-2.4 0Z"/>',
  },
  shifts: {
    label: 'shifts',
    one: 'shift',
    colour: '#e0b45a',
    art: '<path d="M4.4 2.2h7.2M4.4 13.8h7.2"/><path d="M5.9 2.2v2.5L8 8l2.1-3.3V2.2"/><path d="M5.9 13.8v-2.5L8 8l2.1 3.3v2.5"/>',
  },

  // A die, for a yield that is rolled rather than counted. Matt: "maybe we
  // need an icon to indicate like a multiplier by the die."
  dice: {
    label: 'dice', colour: '#efe3c6',
    art: '<rect x="2.5" y="2.5" width="11" height="11" rx="2.5"/><circle cx="8" cy="8" r="1.25" fill="currentColor" stroke="none"/><circle cx="5" cy="5" r="1.1" fill="currentColor" stroke="none"/><circle cx="11" cy="11" r="1.1" fill="currentColor" stroke="none"/>',
  },
  // Not a resource you hold - a card drawn, so "draw 1" can be a pip like
  // everything else instead of two words.
  cards: {
    label: 'cards',
    one: 'card',
    colour: '#e6dcc0',
    // Two fanned cards, no lines on them: the old single card with two
    // ruled lines read as a page of the knowledge book (playtest 11).
    art: '<rect x="1.6" y="4" width="7.4" height="10.4" rx="1.2" transform="rotate(-12 5.3 9.2)"/><rect x="6.6" y="2.2" width="7.4" height="10.4" rx="1.2" transform="rotate(10 10.3 7.4)"/>',
  },
  threat: {
    label: 'threat',
    colour: '#e0796a',
    art: '<path d="M8 1.6 13.6 4v4.2c0 3.2-2.3 5.6-5.6 6.4-3.3-.8-5.6-3.2-5.6-6.4V4Z"/>',
  },
};

export const SPENDABLE = ['workers', 'food', 'goods', 'coin', 'knowledge'];

export const artForResource = (key) => RESOURCES[key]?.art || RESOURCES.goods.art;
export const labelFor = (key, n) => {
  const r = RESOURCES[key];
  if (!r) return key;
  return n === 1 && r.one ? r.one : r.label;
};
